import React from 'react';
import { Twitter, Linkedin, Github, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { scrollToId } from '../lib/scroll';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: 'Features', href: '#features' },
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'Coverage Calculator', href: '#tools' },
      { label: 'Glossary', href: '#glossary' }
    ],
    company: [
      { label: 'About', href: '#about' },
      { label: 'Contact', href: '#contact' }
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy', route: true },
      { label: 'Terms of Service', href: '/terms', route: true }
    ]
  };

  const socialLinks = [
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Github, href: '#', label: 'GitHub' },
    { icon: Mail, href: '#contact', label: 'Email' }
  ];

  const renderLink = (link, index) => {
    if (link.route) {
      return (
        <Link
          key={index}
          to={link.href}
          className="text-slate-500 hover:text-cyan-400 transition-colors duration-200 text-sm"
        >
          {link.label}
        </Link>
      );
    }
    return (
      <button
        key={index}
        onClick={() => scrollToId(link.href.replace('#', ''))}
        className="text-slate-500 hover:text-cyan-400 transition-colors duration-200 text-sm"
      >
        {link.label}
      </button>
    );
  };

  return (
    <footer data-testid="site-footer" className="bg-ink text-white border-t rule-dark">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-10">
        {/* Big wordmark */}
        <div className="pb-14 mb-14 border-b rule-dark">
          <img src="/symbionic-logo.png" alt="Symbionic" className="h-14 w-auto mb-6" />
          <p className="text-slate-500 leading-relaxed max-w-md text-lg">
            Building intelligent insurance applications that make protection simple, 
            transparent, and actually useful for everyone.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-14">
          <div>
            <h3 className="eyebrow text-slate-500 mb-5">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link, index) => (
                <li key={index}>{renderLink(link, index)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="eyebrow text-slate-500 mb-5">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>{renderLink(link, index)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="eyebrow text-slate-500 mb-5">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>{renderLink(link, index)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="eyebrow text-slate-500 mb-5">Connect</h3>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 border border-white/10 hover:border-cyan-400/50 rounded-xl flex items-center justify-center transition-all duration-300 group"
                >
                  <social.icon className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t rule-dark">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-600 text-sm">
              © {currentYear} Symbionic. All rights reserved.
            </p>
            <p className="text-slate-600 text-sm">
              <a href="https://verifaeye.com.au" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">VerifAEye™</a> — Turn your smartphone into a forensic insurance assessor
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
